import { useEffect, useState } from "react";
import {
  Lock, Loader2, Upload, RefreshCw, Ban, ShieldCheck, Search,
  TrendingUp, Users, Send, FileBox, ArrowLeft, Trash2, Settings, Save, Mail, Check, KeyRound,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "dppg_admin_token";

function api(path, opts = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", "X-Admin-Token": token || "", ...(opts.headers || {}) },
  }).then((r) => {
    // Stale/invalid admin token → force re-login
    if (r.status === 401 && token) {
      localStorage.removeItem(TOKEN_KEY);
      if (typeof window !== "undefined") window.location.reload();
    }
    return r;
  });
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  return (
    <div data-testid="admin-page" className="relative min-h-screen overflow-hidden">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[760px] h-[760px] gold-ring rounded-full pointer-events-none" />
      <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <a href="/" className="inline-flex items-center gap-2 text-[12px] font-mono uppercase tracking-widest text-[color:var(--ink-mute)] hover:text-[color:var(--gold)] transition-colors mb-6">
          <ArrowLeft className="w-3.5 h-3.5" />Back to homepage
        </a>
        <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
          <span className="text-[color:var(--gold)] glow-gold">Admin</span> Console
        </h1>
        {!token ? <Login onLogin={(t) => { setToken(t); localStorage.setItem(TOKEN_KEY, t); }} /> : <Dashboard onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} />}
      </div>
    </div>
  );
}

function Login({ onLogin }) {
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/admin/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pwd }),
      });
      const d = await r.json();
      if (!r.ok) setErr(d.detail || "Wrong password.");
      else onLogin(d.token);
    } catch { setErr("Network error."); } finally { setBusy(false); }
  };
  return (
    <div className="mt-8 max-w-sm panel p-6 sm:p-8">
      <div className="flex items-center gap-2 mb-4"><Lock className="w-5 h-5 text-[color:var(--gold)]" /><div className="font-display font-bold text-base">Admin login</div></div>
      <form onSubmit={submit} className="space-y-4">
        <input type="password" placeholder="Admin password" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="admin-password"
          className="w-full bg-[#0e0e12] border border-[#23232a] rounded-lg px-4 h-11 text-white placeholder:text-[color:var(--ink-mute)] focus:border-[color:var(--gold)]/60 focus:outline-none transition-colors" />
        {err && <div className="text-[13px] text-red-400">{err}</div>}
        <button type="submit" disabled={busy} data-testid="admin-login-btn" className="btn-gold w-full h-11 rounded-full inline-flex items-center justify-center gap-2 text-[13px] font-display">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}Login
        </button>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("dashboard");
  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: TrendingUp },
    { id: "licenses", label: "Licenses", icon: Users },
    { id: "manual", label: "Manual Issue", icon: KeyRound },
    { id: "versions", label: "EA Versions", icon: FileBox },
    { id: "blast", label: "Email Blast", icon: Send },
    { id: "settings", label: "Settings", icon: Settings },
  ];
  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 flex-wrap mb-6 border-b border-[#23232a] pb-3">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
            className={"px-4 h-9 rounded-full inline-flex items-center gap-2 text-[12.5px] font-display transition-colors " + (tab === id ? "bg-[color:var(--gold)]/15 text-[color:var(--gold)] border border-[color:var(--gold)]/45" : "border border-[#23232a] text-[color:var(--ink-dim)] hover:border-[color:var(--gold)]/30")}>
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
        <button onClick={onLogout} className="ml-auto px-4 h-9 rounded-full inline-flex items-center gap-2 text-[12.5px] font-display text-[color:var(--ink-mute)] hover:text-white transition-colors">Logout</button>
      </div>
      {tab === "dashboard" && <Stats />}
      {tab === "licenses" && <Licenses />}
      {tab === "manual" && <ManualIssue />}
      {tab === "versions" && <Versions />}
      {tab === "blast" && <Blast />}
      {tab === "settings" && <SettingsPanel />}
    </div>
  );
}

// ---------- Settings ----------
const SETTING_GROUPS = [
  {
    title: "Email templates",
    icon: Mail,
    keys: [
      { key: "email.subject_purchase", label: "Purchase subject", help: "Variables: {license_id}", type: "text" },
      { key: "email.subject_followup", label: "Follow-up subject", type: "text" },
      { key: "email.cc", label: "CC (optional)", type: "text" },
      { key: "email.bcc", label: "BCC (optional)", type: "text" },
    ],
    actions: [{ id: "test-email", label: "Send test email to me", icon: Mail }],
  },
  {
    title: "AiSensy WhatsApp",
    icon: Send,
    keys: [
      { key: "aisensy.enabled", label: "Enable AiSensy", type: "bool" },
      { key: "aisensy.purchase_template", label: "Purchase template name", help: "Must match the template name approved in AiSensy dashboard.", type: "text" },
      { key: "aisensy.cart_template", label: "Abandoned-cart template name", type: "text" },
    ],
  },
  {
    title: "Marketing automations",
    icon: TrendingUp,
    keys: [
      { key: "marketing.cron_enabled", label: "Enable abandoned-cart cron", type: "bool" },
      { key: "marketing.followup_hours_min", label: "Min hours before follow-up", type: "number" },
      { key: "marketing.followup_hours_max", label: "Max hours window", type: "number" },
      { key: "telegram.enabled", label: "Telegram sales alerts", type: "bool" },
    ],
  },
  {
    title: "Pricing & site",
    icon: ShieldCheck,
    keys: [
      { key: "pricing.show_launch_offer", label: "Show launch offer banner", type: "bool" },
      { key: "pricing.copies_left", label: "Copies-left counter", type: "number" },
      { key: "site.show_sticky_cta", label: "Show mobile sticky CTA", type: "bool" },
      { key: "site.maintenance_mode", label: "Maintenance mode (blocks /thank-you)", type: "bool" },
    ],
  },
];

function SettingsPanel() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState({});
  const [savedKey, setSavedKey] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [testResult, setTestResult] = useState(null);

  const refresh = () => api("/api/admin/settings").then(r => r.json()).then(setData);
  useEffect(() => { refresh(); }, []);

  const update = (key, value) => {
    setSaving(s => ({ ...s, [key]: true }));
    api(`/api/admin/settings/${key}`, { method: "POST", body: JSON.stringify({ value }) })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(() => { setSavedKey(key); setTimeout(() => setSavedKey(""), 1800); refresh(); })
      .catch(() => alert("Failed to save"))
      .finally(() => setSaving(s => ({ ...s, [key]: false })));
  };

  const runAction = async (id) => {
    setTestResult(null);
    if (id === "test-email") {
      if (!testEmail) return alert("Enter your email above");
      const r = await api("/api/admin/settings/_actions/test-email", { method: "POST", body: JSON.stringify({ to: testEmail, template: "purchase" }) });
      setTestResult(await r.json());
    }
  };

  if (!data) return <div className="text-[color:var(--ink-mute)]">Loading settings…</div>;
  const map = {};
  (data.settings || []).forEach(s => { map[s.key] = s; });

  return (
    <div data-testid="settings-panel" className="space-y-6">
      <div className="panel p-5 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex-1">
          <div className="font-display font-bold text-white">Live settings</div>
          <div className="text-[12px] text-[color:var(--ink-mute)] mt-0.5">Changes take effect immediately. No redeploy needed.</div>
        </div>
        <input type="email" placeholder="your@email.com (for tests)" value={testEmail} onChange={e => setTestEmail(e.target.value)} data-testid="settings-test-email"
          className="bg-[#0a0a0b] border border-[#23232a] rounded-lg px-4 h-10 text-white text-[13px] w-full sm:w-64" />
      </div>

      {SETTING_GROUPS.map((g) => {
        const Icon = g.icon;
        return (
          <div key={g.title} className="panel p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[#23232a]">
              <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: "linear-gradient(135deg, rgba(245,208,106,.18), rgba(138,106,21,.05))", border: "1px solid rgba(231,180,59,.35)" }}>
                <Icon className="w-4 h-4 text-[color:var(--gold)]" />
              </div>
              <div className="font-display font-bold text-white">{g.title}</div>
              {g.actions && g.actions.map(a => {
                const A = a.icon;
                return <button key={a.id} onClick={() => runAction(a.id)} data-testid={`run-${a.id}`}
                  className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[color:var(--gold)]/40 text-[color:var(--gold)] hover:bg-[color:var(--gold)]/10 text-[11.5px] font-display transition-colors">
                  <A className="w-3 h-3" />{a.label}
                </button>;
              })}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {g.keys.map(({ key, label, help, type }) => {
                const cur = map[key];
                const val = cur ? cur.value : "";
                const inputClass = "w-full bg-[#0a0a0b] border border-[#23232a] rounded-lg px-3 h-10 text-white text-[13px] focus:border-[color:var(--gold)]/50 focus:outline-none transition-colors";
                return (
                  <label key={key} className="block">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)]">{label}</span>
                      {savedKey === key && <Check className="w-3 h-3 text-[color:var(--green)]" />}
                      {saving[key] && <Loader2 className="w-3 h-3 animate-spin text-[color:var(--gold)]" />}
                    </div>
                    {type === "bool" ? (
                      <button onClick={() => update(key, !val)} data-testid={`set-${key}`}
                        className={"h-10 px-4 rounded-full inline-flex items-center gap-2 text-[12.5px] font-display border transition-colors " + (val ? "bg-[color:var(--green)]/15 text-[color:var(--green)] border-[color:var(--green)]/45" : "bg-[#0a0a0b] text-[color:var(--ink-dim)] border-[#23232a]")}>
                        {val ? "ON" : "OFF"}
                      </button>
                    ) : type === "textarea" ? (
                      <textarea defaultValue={val || ""} rows={3} data-testid={`set-${key}`}
                        onBlur={(e) => e.target.value !== val && update(key, e.target.value)}
                        className={inputClass + " h-auto py-2 resize-y leading-relaxed"} />
                    ) : type === "number" ? (
                      <input type="number" defaultValue={val ?? 0} data-testid={`set-${key}`}
                        onBlur={(e) => Number(e.target.value) !== val && update(key, Number(e.target.value))} className={inputClass} />
                    ) : (
                      <input type="text" defaultValue={val || ""} data-testid={`set-${key}`}
                        onBlur={(e) => e.target.value !== val && update(key, e.target.value)} className={inputClass} />
                    )}
                    {help && <div className="text-[11px] text-[color:var(--ink-mute)] mt-1 leading-relaxed">{help}</div>}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}

      {testResult && (
        <div data-testid="settings-test-result" className="panel p-4">
          <div className="font-display font-bold text-white mb-2">Test result</div>
          <pre className="text-[11.5px] text-[color:var(--ink-dim)] whitespace-pre-wrap break-words">{JSON.stringify(testResult, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

function Stats() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api("/api/admin/dashboard")
      .then(r => r.ok ? r.json() : null)
      .then((d) => setData(d || {}));
  }, []);
  if (!data) return <div className="text-[color:var(--ink-mute)]">Loading…</div>;
  const items = [
    { label: "Total licenses", value: data.total_licenses ?? 0 },
    { label: "Total activations", value: data.total_activations ?? 0 },
    { label: "EA versions uploaded", value: data.total_versions ?? 0 },
    { label: "Current version", value: data.current_version || "—" },
    { label: "Blocked licenses", value: data.total_blocked ?? 0 },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {items.map(({ label, value }) => (
          <div key={label} className="panel p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)]">{label}</div>
            <div className="font-display font-bold text-[28px] text-white mt-1">{value}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 panel p-5 sm:p-6">
        <div className="font-display font-bold text-[14px] mb-3">Recent licenses</div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-left text-[color:var(--ink-mute)] font-mono text-[10px] uppercase tracking-widest">
              <tr><th className="py-2 pr-3">License</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Bindings</th><th className="py-2">Created</th></tr>
            </thead>
            <tbody>
              {(data.recent_licenses || []).map(l => (
                <tr key={l.license_id} className="border-t border-[#1f1f27]">
                  <td className="py-2 pr-3 font-mono text-[color:var(--gold)]">{l.license_id}</td>
                  <td className="py-2 pr-3 text-white">{l.email}</td>
                  <td className="py-2 pr-3">{(l.accounts || []).length}/{l.max_accounts || 10}</td>
                  <td className="py-2 text-[color:var(--ink-mute)]">{new Date(l.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------- Manual Issue ----------
function ManualIssue() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setResult(null);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { setError("Enter a valid email."); return; }
    setSubmitting(true);
    try {
      const r = await api("/api/admin/license/manual-issue", {
        method: "POST",
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          name: name.trim(), phone: phone.trim(), note: note.trim(),
        }),
      });
      const data = await r.json();
      if (!r.ok) setError(data.detail || "Failed to issue license.");
      else {
        setResult(data);
        setEmail(""); setName(""); setPhone(""); setNote("");
      }
    } catch {
      setError("Network error.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="manual-issue-panel" className="space-y-5">
      <div className="panel p-5">
        <div className="font-display font-bold text-white">Manual license issue</div>
        <div className="text-[12px] text-[color:var(--ink-mute)] mt-0.5">
          Use when a customer paid via UPI/external means and the license never landed automatically.
          This sends the same purchase email with all 4 attachments (.ex5, .set, license.bin, EA_User_Guide.pdf).
        </div>
      </div>
      <form onSubmit={submit} className="panel p-5 sm:p-6 space-y-4 max-w-2xl">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)]">Customer email *</span>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="manual-issue-email"
            className="mt-1.5 w-full bg-[#0a0a0b] border border-[#23232a] rounded-lg px-4 h-10 text-white text-[13px] focus:border-[color:var(--gold)]/50 focus:outline-none transition-colors" />
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)]">Customer name</span>
            <input type="text" value={name} onChange={e => setName(e.target.value)} data-testid="manual-issue-name"
              className="mt-1.5 w-full bg-[#0a0a0b] border border-[#23232a] rounded-lg px-4 h-10 text-white text-[13px] focus:border-[color:var(--gold)]/50 focus:outline-none transition-colors" />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)]">WhatsApp number</span>
            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} data-testid="manual-issue-phone"
              className="mt-1.5 w-full bg-[#0a0a0b] border border-[#23232a] rounded-lg px-4 h-10 text-white text-[13px] focus:border-[color:var(--gold)]/50 focus:outline-none transition-colors" />
          </label>
        </div>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)]">Internal note (optional)</span>
          <input type="text" value={note} onChange={e => setNote(e.target.value)} data-testid="manual-issue-note"
            placeholder="e.g. Refund replacement, UPI Order #1234"
            className="mt-1.5 w-full bg-[#0a0a0b] border border-[#23232a] rounded-lg px-4 h-10 text-white text-[13px] focus:border-[color:var(--gold)]/50 focus:outline-none transition-colors" />
        </label>
        {error && <div data-testid="manual-issue-error" className="text-[12.5px] text-red-400 bg-red-500/5 border border-red-500/30 rounded-lg px-4 py-2.5">{error}</div>}
        <button type="submit" disabled={submitting} data-testid="manual-issue-submit"
          className="btn-gold h-11 px-6 rounded-full inline-flex items-center justify-center gap-2 text-[13px] font-display disabled:opacity-50">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Issuing…</> : <><KeyRound className="w-4 h-4" />Issue license & email customer</>}
        </button>
      </form>
      {result && (
        <div data-testid="manual-issue-result" className="panel p-5 sm:p-6 border-[color:var(--green)]/40">
          <div className="flex items-center gap-2 mb-2">
            <Check className="w-4 h-4 text-[color:var(--green)]" />
            <div className="font-display font-bold text-white">License issued</div>
          </div>
          <div className="text-[13px] text-[color:var(--ink-dim)]">
            <div><span className="text-[color:var(--ink-mute)]">License ID:</span> <code className="text-[color:var(--gold)]">{result.license_id}</code></div>
            <div><span className="text-[color:var(--ink-mute)]">Sent to:</span> {result.email}</div>
            <div><span className="text-[color:var(--ink-mute)]">Email delivered:</span> {result.email_sent ? "Yes" : "No (check SMTP logs)"}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function Licenses() {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api(`/api/admin/licenses?q=${encodeURIComponent(q)}&limit=100`);
      const d = await r.json();
      setData({ rows: Array.isArray(d?.rows) ? d.rows : [], total: d?.total || 0 });
    } catch {
      setData({ rows: [], total: 0 });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const action = async (licenseId, kind) => {
    if (!confirm(`${kind} license ${licenseId}?`)) return;
    const path = kind === "reset" ? "/api/admin/licenses/reset" : kind === "block" ? "/api/admin/licenses/block" : "/api/admin/licenses/unblock";
    const r = await api(path, { method: "POST", body: JSON.stringify({ license_id: licenseId }) });
    if (r.ok) load();
    else alert((await r.json()).detail || "Failed.");
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search by email, license, payment_id, name…"
          className="flex-1 bg-[#0e0e12] border border-[#23232a] rounded-lg px-4 h-10 text-white placeholder:text-[color:var(--ink-mute)] focus:border-[color:var(--gold)]/60 focus:outline-none transition-colors" />
        <button onClick={load} className="btn-gold px-5 h-10 rounded-full inline-flex items-center gap-2 text-[12.5px] font-display">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}Search
        </button>
      </div>
      <div className="text-[12px] text-[color:var(--ink-mute)] mb-2">{data.total} total · showing {data.rows.length}</div>
      <div className="space-y-3">
        {data.rows.map(l => (
          <div key={l.license_id} className="panel p-4 sm:p-5">
            <div className="flex items-center gap-3 flex-wrap mb-2">
              <code className="font-mono font-bold text-[13px] text-[color:var(--gold)]">{l.license_id}</code>
              <span className="font-mono text-[11px] text-white">{l.email}</span>
              {l.name && <span className="font-mono text-[11px] text-[color:var(--ink-mute)]">{l.name}</span>}
              {l.blocked && <span className="chip" style={{ color: "#f87171", borderColor: "rgba(248,113,113,.4)" }}>BLOCKED</span>}
              <div className="ml-auto flex gap-2">
                <button onClick={() => action(l.license_id, "reset")} data-testid={`reset-${l.license_id}`} className="px-3 h-8 rounded-md bg-[#23232a] hover:bg-[#2e2e36] text-[11px] font-mono uppercase tracking-wider text-white inline-flex items-center gap-1.5 transition-colors">
                  <RefreshCw className="w-3 h-3" />Reset
                </button>
                {l.blocked ? (
                  <button onClick={() => action(l.license_id, "unblock")} className="px-3 h-8 rounded-md bg-[color:var(--green)]/15 hover:bg-[color:var(--green)]/25 text-[11px] font-mono uppercase tracking-wider text-[color:var(--green)] inline-flex items-center gap-1.5 transition-colors">
                    <ShieldCheck className="w-3 h-3" />Unblock
                  </button>
                ) : (
                  <button onClick={() => action(l.license_id, "block")} className="px-3 h-8 rounded-md bg-red-500/15 hover:bg-red-500/25 text-[11px] font-mono uppercase tracking-wider text-red-400 inline-flex items-center gap-1.5 transition-colors">
                    <Ban className="w-3 h-3" />Block
                  </button>
                )}
              </div>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)] mb-2">
              {(l.accounts || []).length}/{l.max_accounts || 10} accounts · created {new Date(l.created_at).toLocaleString()} · payment {l.payment_id || "—"}
            </div>
            {(l.accounts || []).length > 0 && (
              <div className="grid sm:grid-cols-2 gap-1.5">
                {(l.accounts || []).map((a, i) => (
                  <div key={i} className="text-[11.5px] font-mono bg-[#0a0a0b] border border-[#23232a] rounded px-3 py-1.5">
                    <span className="text-[color:var(--gold)]">#{a.mt5_account}</span>
                    <span className="text-[color:var(--ink-mute)]"> · {a.broker || "—"}</span>
                    <span className="text-[color:var(--ink-mute)] block text-[10px]">bound {new Date(a.bound_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Versions() {
  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const r = await api("/api/admin/versions");
      if (!r.ok) return;
      const d = await r.json();
      setVersions(Array.isArray(d?.rows) ? d.rows : []);
    } catch {
      setVersions([]);
    }
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault(); setMsg(""); setBusy(true);
    try {
      if (!file || !version) { setMsg("File and version required."); setBusy(false); return; }
      const fd = new FormData(); fd.append("file", file);
      const token = localStorage.getItem(TOKEN_KEY);
      const r = await fetch(`${API_BASE}/api/admin/upload-ex5?version=${encodeURIComponent(version)}&notes=${encodeURIComponent(notes)}`, {
        method: "POST", headers: { "X-Admin-Token": token || "" }, body: fd,
      });
      if (r.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setMsg("Session expired. Reloading…");
        setTimeout(() => window.location.reload(), 1200);
        return;
      }
      const d = await r.json();
      if (!r.ok) setMsg(d.detail || "Upload failed.");
      else { setMsg(`Uploaded v${d.version}, hash ${d.hash}`); setVersion(""); setNotes(""); setFile(null); load(); }
    } finally { setBusy(false); }
  };

  const promote = async (v) => {
    const r = await api("/api/admin/versions/promote", { method: "POST", body: JSON.stringify({ version: v }) });
    if (r.ok) load();
  };

  return (
    <div>
      <div className="panel p-5 sm:p-6">
        <div className="font-display font-bold text-[14px] mb-3">Upload new EA version</div>
        <form onSubmit={upload} className="grid sm:grid-cols-2 gap-3">
          <input type="text" placeholder="Version (e.g. 1.24)" value={version} onChange={(e) => setVersion(e.target.value)}
            data-testid="upload-version" required
            className="bg-[#0e0e12] border border-[#23232a] rounded-lg px-4 h-10 text-white placeholder:text-[color:var(--ink-mute)] focus:border-[color:var(--gold)]/60 focus:outline-none transition-colors" />
          <input type="file" accept=".ex5" onChange={(e) => setFile(e.target.files[0])} data-testid="upload-file" required
            className="bg-[#0e0e12] border border-[#23232a] rounded-lg px-4 h-10 text-white text-[12px] file:mr-3 file:bg-[color:var(--gold)] file:text-black file:rounded file:border-none file:px-3 file:py-1 file:font-bold" />
          <textarea placeholder="Release notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)}
            className="sm:col-span-2 bg-[#0e0e12] border border-[#23232a] rounded-lg px-4 py-2 min-h-[60px] text-white placeholder:text-[color:var(--ink-mute)] focus:border-[color:var(--gold)]/60 focus:outline-none transition-colors text-[13px]" />
          <button type="submit" disabled={busy} data-testid="upload-submit" className="sm:col-span-2 btn-gold h-10 rounded-full inline-flex items-center justify-center gap-2 text-[12.5px] font-display">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Upload & promote as current
          </button>
          {msg && <div className="sm:col-span-2 text-[13px] text-[color:var(--green)]">{msg}</div>}
        </form>
      </div>
      <div className="mt-6 panel p-5 sm:p-6">
        <div className="font-display font-bold text-[14px] mb-3">Version history</div>
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[color:var(--ink-mute)] font-mono text-[10px] uppercase tracking-widest">
            <tr><th className="py-2 pr-3">Version</th><th className="py-2 pr-3">Size</th><th className="py-2 pr-3">Hash</th><th className="py-2 pr-3">Uploaded</th><th className="py-2">Status</th></tr>
          </thead>
          <tbody>
            {versions.map(v => (
              <tr key={v.version} className="border-t border-[#1f1f27]">
                <td className="py-2 pr-3 font-mono text-[color:var(--gold)] font-bold">v{v.version}</td>
                <td className="py-2 pr-3">{Math.round((v.file_size || 0) / 1024)} KB</td>
                <td className="py-2 pr-3 font-mono text-[10.5px] text-[color:var(--ink-mute)]">{(v.file_hash || "").slice(0, 12)}</td>
                <td className="py-2 pr-3 text-[color:var(--ink-mute)]">{new Date(v.uploaded_at).toLocaleString()}</td>
                <td className="py-2">
                  {v.is_current ? (
                    <span className="chip" style={{ color: "var(--green)", borderColor: "rgba(0,255,136,.4)" }}>CURRENT</span>
                  ) : (
                    <button onClick={() => promote(v.version)} className="px-3 h-7 rounded-md bg-[#23232a] hover:bg-[#2e2e36] text-[10.5px] font-mono uppercase tracking-wider text-white">Make current</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Blast() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [version, setVersion] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const send = async (e) => {
    e.preventDefault(); setMsg(""); setBusy(true);
    if (!confirm(`Email all customers with subject: "${subject}"?`)) { setBusy(false); return; }
    const r = await api("/api/admin/email-blast", { method: "POST", body: JSON.stringify({ subject, body, notify_version: version }) });
    const d = await r.json();
    if (!r.ok) setMsg(d.detail || "Failed.");
    else setMsg(`Sent: ${d.sent}, failed: ${d.failed}`);
    setBusy(false);
  };
  return (
    <div className="panel p-5 sm:p-6 max-w-2xl">
      <div className="font-display font-bold text-[14px] mb-3">Send email blast to all customers</div>
      <form onSubmit={send} className="space-y-3">
        <input type="text" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} required
          className="w-full bg-[#0e0e12] border border-[#23232a] rounded-lg px-4 h-10 text-white placeholder:text-[color:var(--ink-mute)] focus:border-[color:var(--gold)]/60 focus:outline-none transition-colors" />
        <input type="text" placeholder="Notify about version (optional, e.g. 1.24)" value={version} onChange={(e) => setVersion(e.target.value)}
          className="w-full bg-[#0e0e12] border border-[#23232a] rounded-lg px-4 h-10 text-white placeholder:text-[color:var(--ink-mute)] focus:border-[color:var(--gold)]/60 focus:outline-none transition-colors" />
        <textarea placeholder="Body / release notes" value={body} onChange={(e) => setBody(e.target.value)} rows={6}
          className="w-full bg-[#0e0e12] border border-[#23232a] rounded-lg px-4 py-2 text-white placeholder:text-[color:var(--ink-mute)] focus:border-[color:var(--gold)]/60 focus:outline-none transition-colors text-[13px]" />
        <button type="submit" disabled={busy} className="btn-gold w-full h-11 rounded-full inline-flex items-center justify-center gap-2 text-[13px] font-display">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send blast
        </button>
        {msg && <div className="text-[13px] text-[color:var(--green)]">{msg}</div>}
      </form>
    </div>
  );
}
